<template>
  <div class="wrap">
    <div class="grid">
      <!-- LEFT -->
      <section class="panel">
        <div class="ph">
          <h2>Kredi Kartı</h2>
          <span class="pill">
            <span class="dot" :class="{ ok: !!brand.key }"></span>
            <span>Kart: {{ brand.name }}</span>
          </span>
        </div>

        <div class="card-area">
          <div class="card-3d">
            <div class="card" :class="{ flipped: isFlipped }">
              <div class="face front">
                <div class="top">
                  <div class="chip"></div>
                  <div class="brandText">{{ brand.name }}</div>
                </div>

                <div class="num">{{ numberPreview }}</div>

                <div class="meta">
                  <div>
                    <div class="lbl">Kart Sahibi</div>
                    <div class="val">{{ namePreview }}</div>
                  </div>
                  <div style="text-align:right">
                    <div class="lbl">SKT</div>
                    <div class="val">{{ expPreview }}</div>
                  </div>
                </div>
              </div>

              <div class="face back">
                <div class="mag"></div>
                <div class="sig">
                  <div class="muted" style="font-size:12px;">CVC</div>
                  <div class="cvc-box">{{ cvcPreview }}</div>
                </div>
                <div class="hint">CVC alanına odaklanınca kart arka yüze döner.</div>
              </div>
            </div>
          </div>

          <div class="form">
            <div class="field span2">
              <label>Tahsilat Tutarı (TL)</label>
              <input
                class="input"
                v-model="amountInput"
                inputmode="decimal"
                placeholder="Örn: 1250,00"
                @input="onAmountInput"
              />
            </div>

            <div class="field span2">
              <label>Kart Numarası</label>
              <div
                v-if="!enteredAmount || enteredAmount <= 0"
                class="form-hint form-hint--warn"
              >
                Önce tahsilat tutarı giriniz, ardından kart numarası alanı aktif olacaktır.
              </div>
              <input
                class="input"
                v-model="cardNumber"
                inputmode="numeric"
                autocomplete="cc-number"
                  placeholder="•••• •••• •••• ••••"
                :disabled="!enteredAmount || enteredAmount <= 0"
                @input="onCardNumberInput"
                @focus="handleCardNumberFocus"
              />
            </div>

            <div class="field span2">
              <label>Kart Sahibi</label>
              <input
                class="input"
                v-model="cardName"
                autocomplete="cc-name"
                placeholder="Ad Soyad"
                @input="onNameInput"
              />
            </div>

            <div class="pay-form-grid span2">
              <div class="pay-form-item">
                <label class="pay-label">Ay</label>
                <select class="fa-input" v-model="expMonth" @change="syncExpPreview">
                  <option value="">MM</option>
                  <option v-for="m in 12" :key="'m' + m" :value="pad2(m)">
                    {{ pad2(m) }}
                  </option>
                </select>
              </div>

              <div class="pay-form-item">
                <label class="pay-label">Yıl</label>
                <select class="fa-input" v-model="expYear" @change="syncExpPreview">
                  <option value="">YY</option>
                  <option v-for="yy in yearOptions" :key="'y' + yy" :value="yy">
                    {{ yy }}
                  </option>
                </select>
              </div>

              <div class="pay-form-item">
                <label class="pay-label">CVC</label>
                <input
                  class="fa-input"
                  v-model="cvc"
                  inputmode="numeric"
                  :maxlength="brand.cvc"
                  :placeholder="brand.cvc === 4 ? '••••' : '•••'"
                  @focus="isFlipped = true"
                  @blur="isFlipped = false"
                  @input="onCvcInput"
                />
              </div>

              <div class="pay-form-item pay-form-item--dummy"></div>
            </div>
          </div>
        </div>
      </section>

      <!-- MIDDLE -->
      <section class="panel">
        <div class="ph">
          <h2>Taksit Seçimi</h2>
          <span class="pill">
            <span class="dot" :class="{ ok: !!brand.key }"></span>
            <span>{{ brand.key ? ("Sadece " + brand.name + " için") : "Kart giriniz" }}</span>
          </span>
        </div>

        <div class="mid">
          <div class="info">
            <div>
              <div class="k">Uygun Kart</div>
              <div class="v">{{ brand.name }}</div>
            </div>

            <div v-if="binCheckLoading" style="text-align:right;">
              <div class="k">Durum</div>
              <div class="v">Kart sorgulanıyor...</div>
            </div>

            <div v-else-if="binInfo || providerInfo" style="text-align:right;">
              <div class="k">Banka / Provider</div>
              <div class="v">
                {{ currentBankName || '-' }} /
                {{ currentProviderName || '-' }}
              </div>
            </div>
          </div>

          <div
            v-if="binCheckError"
            class="empty"
            style="border-color:#fecaca;color:#b91c1c;background:#fff5f5;"
          >
            {{ binCheckError }}
          </div>

          <div
            v-if="payError"
            class="empty"
            style="border-color:#fecaca;color:#b91c1c;background:#fff5f5;"
          >
            {{ payError }}
          </div>

          <div
            v-if="paySuccess"
            class="empty"
            style="border-color:#bbf7d0;color:#15803d;background:#f0fdf4;"
          >
            {{ paySuccess }}
          </div>

          <div v-if="!brand.key" class="empty">
            Kart numarasını girince, sadece o karta uygun taksitler listelenecek.
          </div>

          <div v-else class="inst-list">
            <div
              v-for="opt in installmentOptions"
              :key="'inst-' + opt.n"
              class="plan-card"
              :class="{ active: selectedInstallment && selectedInstallment.n === opt.n }"
              @click="selectInstallment(opt)"
            >
              <div class="plan-left">
                <div class="plan-number">{{ opt.n }}</div>

                <div class="plan-text">
                  <div class="plan-title">
                    {{ opt.n === 1 ? 'Tek çekim' : opt.n + ' taksit' }}
                  </div>

                  <div class="plan-sub">
                    <template v-if="opt.rate > 0">
                      Vade farkı: {{ fmtMoney(opt.feeAmount) }}
                    </template>
                    <template v-else>
                      Vade farkı yok
                    </template>
                  </div>
                </div>
              </div>

              <div class="plan-right">
                <div class="plan-monthly">{{ fmtMoney(opt.monthly) }} / ay</div>
                <div class="plan-total">Toplam: {{ fmtMoney(opt.total) }}</div>
              </div>

              <div class="plan-radio">
                <span
                  class="radio-dot"
                  :class="{ active: selectedInstallment && selectedInstallment.n === opt.n }"
                ></span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- RIGHT -->
      <section class="panel">
        <div class="ph">
          <h2>Ödeme Özeti</h2>
          <span class="pill">
            <span class="dot ok"></span>
            <span>Güvenli</span>
          </span>
        </div>

        <div class="sum">
          <div class="brandbox">
            <div class="brandtop">
              <div class="logo" style="overflow: hidden;">
                <img src="../../static/avatar.jpeg" alt="">
              </div>
              <div class="brandtxt">
                <div class="h">
                  <span v-if="$store.aktifCari.plasiyer == 'null null'">Satış Temsilcisi</span>
                  <span v-else style="font-size:17px">{{ $store.aktifCari.plasiyer }}</span>
                </div>
                <span class="s">{{ $store.aktifCari.ptel || '0 (5xx) xxx xx xx' }}</span>
              </div>
            </div>

            <div class="linkbox">
              <div>{{ hesap.isim }}</div>
              <div title="Aç">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M14 3h7v7" stroke="#64748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M10 14L21 3" stroke="#64748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" stroke="#64748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>

            <div class="lines">
              <div class="line">
                <div class="k">Bakiye</div>
                <div class="v">{{ fmtMoney(enteredAmount) }}</div>
              </div>

              <div class="line">
                <div class="k">Taksit</div>
                <div class="v muted">
                  {{ paymentInstallment === 1 ? 'Tek çekim' : (paymentInstallment + ' taksit') }}
                </div>
              </div>

              <div class="line">
                <div class="k">Aylık Ödeme</div>
                <div class="v">{{ fmtMoney(summaryMonthly) }}</div>
              </div>

              <div class="line">
                <div class="k">Ek Masraf</div>
                <div class="v">{{ fmtMoney(paymentFeeAmount) }}</div>
              </div>

              <div class="line">
                <div class="k">Toplam</div>
                <div class="v">{{ fmtMoney(paymentAmount) }}</div>
              </div>
            </div>

            <button class="paybtn" type="button" @click="pay" :disabled="payLoading || binCheckLoading">
              {{ payLoading ? "Ödeme başlatılıyor..." : "Güvenli Ödeme Yap" }}
              <span class="lock" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M7 11V8a5 5 0 0 1 10 0v3" stroke="white" stroke-width="2" stroke-linecap="round"/>
                  <path d="M6 11h12a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z" stroke="white" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </span>
            </button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  name: "PaymentPage",
  props: {
    basketTotal: { type: Number, default: 0 },
    currency: { type: String, default: "₺" }
  },
  data() {
    const now = new Date();
    const y = now.getFullYear();

    return {
      hesap: this.$request.ayar(),

      cardNumber: "",
      cardName: "",
      expMonth: this.pad2(now.getMonth() + 1),
      expYear: String(y).slice(-2),
      cvc: "",
      isFlipped: false,

      numberPreview: "•••• •••• •••• ••••",
      namePreview: "AD SOYAD",
      expPreview: this.pad2(now.getMonth() + 1) + "/" + String(y).slice(-2),
      cvcPreview: "•••",

      brand: { name: "-", key: null, cvc: 3, group: [4, 4, 4, 4], max: 16 },

      selectedInstallment: null,

      binInfo: null,
      providerInfo: null,
      backendInstallments: [],
      binCheckLoading: false,
      payLoading: false,
      binCheckError: "",
      payError: "",
      paySuccess: "",
      lastBinQueried: "",
      binCheckTimer: null,

      lastOrderNo: "",
      lastRedirectUrl: "",

      amountInput: "",

    };
  },

  created() {
    this.$store.setLoading([false, ""]);
  },

  mounted() {
    this.syncExpPreview();
  },

  computed: {
    yearOptions() {
      const now = new Date();
      const y = now.getFullYear();
      const arr = [];
      for (let i = 0; i < 12; i++) arr.push(String(y + i).slice(-2));
      return arr;
    },

    currentBin() {
      return this.onlyDigits(this.cardNumber).slice(0, 8);
    },

    enteredAmount() {
      const raw = String(this.amountInput || "")
        .replace(/\./g, "")
        .replace(",", ".");
      const n = Number(raw);
      return Number.isFinite(n) ? n : 0;
    },

    currentBankName() {
      if (this.binInfo && this.binInfo.name) return this.binInfo.name;
      if (this.selectedInstallment && this.selectedInstallment.meta && this.selectedInstallment.meta.BankName) {
        return this.selectedInstallment.meta.BankName;
      }
      if (this.installmentOptions.length && this.installmentOptions[0].meta && this.installmentOptions[0].meta.BankName) {
        return this.installmentOptions[0].meta.BankName;
      }
      return "";
    },

    currentProviderName() {
      if (this.providerInfo && this.providerInfo.name) return this.providerInfo.name;
      if (this.providerInfo && this.providerInfo.code) return this.providerInfo.code;
      return "";
    },

    installmentOptions() {
      if (!this.backendInstallments || !this.backendInstallments.length) return [];

      return this.backendInstallments.map((r) => {
        const installment = Number(r.installment || 1);
        const total = Number(r.total || this.enteredAmount || 0);
        const feeAmount = installment === 1 ? 0 : Number(r.feeAmount || 0);
        const monthly =
          Number(r.monthly || 0) ||
          Number((total / installment).toFixed(2));

        return {
          n: installment,
          installment,
          feeAmount,
          total,
          monthly,
          rate: installment === 1 ? 0 : Number(r.rate || 0),
          meta: r.meta || null
        };
      });
    },

    paymentAmount() {
      if (
        this.selectedInstallment &&
        Number(this.selectedInstallment.installment || this.selectedInstallment.n || 1) > 1
      ) {
        return Number(this.selectedInstallment.total || 0);
      }

      return Number(this.enteredAmount || 0);
    },

    paymentFeeAmount() {
      if (
        this.selectedInstallment &&
        Number(this.selectedInstallment.installment || this.selectedInstallment.n || 1) > 1
      ) {
        return Number(this.selectedInstallment.feeAmount || 0);
      }

      return 0;
    },

    paymentInstallment() {
      if (!this.selectedInstallment) return 1;
      return Number(this.selectedInstallment.installment || this.selectedInstallment.n || 1);
    },

    summaryMonthly() {
      if (this.selectedInstallment) {
        return Number(this.selectedInstallment.monthly || 0);
      }
      return Number(this.enteredAmount || 0);
    }
  },

  watch: {
    "brand.key"(v) {
      this.selectedInstallment = null;
      if (v && this.installmentOptions.length) {
        this.selectedInstallment = this.installmentOptions[0] || null;
      }
    }
  },

  beforeDestroy() {
    if (this.binCheckTimer) {
      clearTimeout(this.binCheckTimer);
      this.binCheckTimer = null;
    }
  },

  methods: {
    pad2(n) {
      return String(n).padStart(2, "0");
    },

    onlyDigits(s) {
      return (s || "").replace(/\D/g, "");
    },

    fmtMoney(n) {
      const x = Number(n || 0);
      return this.currency + " " + x.toLocaleString("tr-TR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    detectBrand(digits) {
      if (!digits) return { name: "•", key: null, cvc: 3, group: [4, 4, 4, 4], max: 16 };
      if (/^4/.test(digits)) return { name: "VISA", key: "visa", cvc: 3, group: [4, 4, 4, 4], max: 16 };
      if (/^(5[1-5])/.test(digits) || /^(2(2[2-9]|[3-6]\d|7[01]|720))/.test(digits)) {
        return { name: "MASTERCARD", key: "mc", cvc: 3, group: [4, 4, 4, 4], max: 16 };
      }
      if (/^(34|37)/.test(digits)) return { name: "AMEX", key: "amex", cvc: 4, group: [4, 6, 5], max: 15 };
      if (/^9792/.test(digits)) return { name: "TROY", key: "troy", cvc: 3, group: [4, 4, 4, 4], max: 16 };
      return { name: "KART", key: "other", cvc: 3, group: [4, 4, 4, 4], max: 16 };
    },

    formatByGroups(digits, groups) {
      let out = [];
      let i = 0;
      for (const g of groups) {
        if (i >= digits.length) break;
        out.push(digits.slice(i, i + g));
        i += g;
      }
      if (i < digits.length) out.push(digits.slice(i));
      return out.join(" ");
    },

    maskedPreview(digits, brand) {
      const shown = digits.slice(-4);
      const maskedLen = Math.max(0, Math.min(digits.length, brand.max) - 4);
      const masked = "•".repeat(maskedLen) + shown;
      return this.formatByGroups(masked, brand.group);
    },

    syncExpPreview() {
      const mm = this.expMonth || "MM";
      const yy = this.expYear || "YY";
      this.expPreview = mm + "/" + yy;
    },

    onNameInput() {
      const v = (this.cardName || "").trim().toUpperCase();
      this.namePreview = v ? v : "AD SOYAD";
    },

    onCvcInput() {
      const d = this.onlyDigits(this.cvc).slice(0, this.brand.cvc);
      this.cvc = d;
      this.cvcPreview = d
        ? "•".repeat(Math.max(0, d.length - 1)) + d.slice(-1)
        : (this.brand.cvc === 4 ? "••••" : "•••");
    },

    handleCardNumberFocus() {
      if (!this.enteredAmount || this.enteredAmount <= 0) {
        this.payError = "Önce tahsilat tutarı girilmelidir.";
      }
    },

    onAmountInput() {
      this.scheduleBinCheck();

      if (!this.enteredAmount || this.enteredAmount <= 0) {
        this.cardNumber = "";
        this.numberPreview = "•••• •••• •••• ••••";
        this.backendInstallments = [];
        this.selectedInstallment = null;
        this.binInfo = null;
        this.providerInfo = null;
        this.lastBinQueried = "";
        this.binCheckError = "";
      }
    },

    onCardNumberInput(e) {
      const input = e.target;
      const start = input.selectionStart || 0;
      const before = input.value;
      const digitsBefore = this.onlyDigits(before.slice(0, start)).length;

      const digitsRaw = this.onlyDigits(this.cardNumber);
      const b = this.detectBrand(digitsRaw);
      this.brand = b;

      const digits = digitsRaw.slice(0, b.max);
      this.cardNumber = this.formatByGroups(digits, b.group);

      this.numberPreview = digits.length
        ? this.maskedPreview(digits, b)
        : "•••• •••• •••• ••••";

      this.$nextTick(() => {
        const after = input.value;
        let pos = 0;
        let seen = 0;
        while (pos < after.length && seen < digitsBefore) {
          if (/\d/.test(after[pos])) seen++;
          pos++;
        }
        input.setSelectionRange(pos, pos);
      });

      this.scheduleBinCheck();
    },

    scheduleBinCheck() {
      this.binCheckError = "";
      this.payError = "";
      this.paySuccess = "";

      const bin = this.currentBin;

      if (this.binCheckTimer) {
        clearTimeout(this.binCheckTimer);
        this.binCheckTimer = null;
      }

      if (!this.enteredAmount || this.enteredAmount <= 0) {
        return;
      }

      if (bin.length < 6) {
        this.backendInstallments = [];
        this.binInfo = null;
        this.providerInfo = null;
        this.selectedInstallment = null;
        this.lastBinQueried = "";
        return;
      }

      this.binCheckTimer = setTimeout(() => {
        this.fetchBinInfo();
      }, 450);
    },

    async fetchBinInfo() {
      const bin = this.currentBin;

      if (bin.length < 6) return;
      if (!this.enteredAmount || this.enteredAmount <= 0) return;

      this.binCheckLoading = true;
      this.binCheckError = "";

      try {
        const payload = {
          bin: bin,
          amount: Number(this.enteredAmount || 0),
          currency: "TRY"
        };
console.log(payload)
        const res = await this.$request.post("payment/bin-check", payload);
        const data = res && res.data ? res.data : res;

        if (!data || data.success === false) {
          throw new Error((data && data.message) || "BIN sorgusu başarısız");
        }

        this.lastBinQueried = bin;
        this.binInfo = data.bank || null;
        this.providerInfo = data.provider || null;
        this.backendInstallments = Array.isArray(data.installments) ? data.installments : [];

        if (this.backendInstallments.length) {
          this.selectedInstallment = this.installmentOptions[0] || null;
        } else {
          this.selectedInstallment = null;
          this.binCheckError = "Bu kart için kullanılabilir taksit bulunamadı";
        }
      } catch (err) {
        console.error("BIN_CHECK_ERROR", err);
        this.backendInstallments = [];
        this.selectedInstallment = null;
        this.binInfo = null;
        this.providerInfo = null;
        this.binCheckError =
          (err && err.message) ||
          "Kart bilgisi okunamadı, lütfen tekrar deneyin";
      } finally {
        this.binCheckLoading = false;
      }
    },

    selectInstallment(opt) {
      this.selectedInstallment = {
        installment: Number(opt.installment || opt.n || 1),
        n: Number(opt.installment || opt.n || 1),
        rate: Number(opt.rate || 0),
        feeAmount: Number(opt.feeAmount || 0),
        total: Number(opt.total || this.enteredAmount || 0),
        monthly: Number(opt.monthly || 0),
        meta: opt.meta || null
      };
    },

    buildOrderNo() {
      const d = new Date();
      const y = d.getFullYear();
      const m = this.pad2(d.getMonth() + 1);
      const day = this.pad2(d.getDate());
      const hh = this.pad2(d.getHours());
      const mm = this.pad2(d.getMinutes());
      const ss = this.pad2(d.getSeconds());
      return "PMT-" + y + m + day + "-" + hh + mm + ss;
    },

    buildClientOrderReference() {
      return "PINR-" + Date.now();
    },

    getReturnUrl(orderNo, clientOrderReference) {
      const providerCode = String(
        (this.providerInfo && (this.providerInfo.code || this.providerInfo.provider_code)) ||
          "finrota"
      ).toLowerCase();

      // Banka/3D sayfalari query parametrelerini hash\'in onune ekleyebiliyor (SPA hash route bozuluyor).
      // Bu yuzden 3D returnUrl\'i backend callback\'e veriyoruz. Backend de bizi tekrar frontend\'e yonlendiriyor.
      const requestDomain = (this.$request && this.$request.domain) ? String(this.$request.domain) : "";
      const apiOrigin = requestDomain
        ? requestDomain.replace(/\/+$/, "")
        : (window.location.protocol + "//" + window.location.hostname + ":3003");

      const fe =
        window.location.origin +
        window.location.pathname +
        "#/tahsilat/sonuc";

      // `fe` icinde `#` oldugu icin bazi bankalar decode edip fragment\'e ceviriyor (server\'a gitmiyor).
      // Bu nedenle tum provider\'lar icin base64url ile tasiyoruz.
      const fe64 = btoa(unescape(encodeURIComponent(fe)))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/g, "");

      return (
        apiOrigin +
        "/payment/callback/" +
        encodeURIComponent(providerCode) +
        "?orderNo=" +
        encodeURIComponent(orderNo || "") +
        "&ref=" +
        encodeURIComponent(clientOrderReference || "") +
        "&fe64=" +
        encodeURIComponent(fe64)
      );
    },

    validateForm() {
      const numDigits = this.onlyDigits(this.cardNumber);
      const nm = (this.cardName || "").trim();
      const mm = this.expMonth;
      const yy = this.expYear;
      const cv = this.onlyDigits(this.cvc);

      const b = this.detectBrand(numDigits);
      const errs = [];

      if (!b.key) errs.push("Kart numarası giriniz.");
      else if (numDigits.length !== b.max) errs.push("Kart numarası eksik/uygunsuz.");
      if (!nm) errs.push("Kart sahibi gerekli.");
      if (!mm || !yy) errs.push("Son kullanım tarihi gerekli.");
      if (cv.length !== b.cvc) errs.push("CVC eksik.");
      if (!this.selectedInstallment) errs.push("Taksit seçimi gerekli.");
      if (!this.enteredAmount || this.enteredAmount <= 0) {
        errs.push("Tahsilat tutarı gerekli.");
      }

      return {
        ok: errs.length === 0,
        errors: errs,
        brand: b,
        numDigits,
        nm,
        mm,
        yy,
        cv
      };
    },

    async pay() {
      this.payError = "";
      this.paySuccess = "";

      const form = this.validateForm();
      if (!form.ok) {
        this.payError = form.errors.join("\n");
        alert(this.payError);
        return;
      }

      if (!this.lastBinQueried || this.lastBinQueried !== this.currentBin) {
        await this.fetchBinInfo();
        if (!this.selectedInstallment) {
          this.payError = "Ödeme öncesi kart doğrulaması tamamlanamadı";
          alert(this.payError);
          return;
        }
      }

      this.payLoading = true;
      this.$store.setLoading([true, "Ödeme başlatılıyor"]);

      try {
        const orderNo = this.buildOrderNo();
        const clientOrderReference = this.buildClientOrderReference();
        const returnUrl = this.getReturnUrl(orderNo, clientOrderReference);
        console.log("[tahsilat/add] providerCode for returnUrl =", String((this.providerInfo && (this.providerInfo.code || this.providerInfo.provider_code)) || "finrota").toLowerCase());
        console.log("[tahsilat/add] returnUrl =", returnUrl);

        const aktifCari = (this.$store && this.$store.aktifCari) || {};
        console.log(aktifCari)
        const carid = (aktifCari.guid || "");
        const customerEmail = (aktifCari.mail || aktifCari.email || "").trim();
        const customerPhone = (aktifCari.gsm || "").trim();
        const baslik = (aktifCari.unvan || "").trim();
        const vno = (aktifCari.vno || "").trim();
        const payload = {
          amount: Number(this.enteredAmount || 0),
          feeAmount: 0,
          installment: Number(this.paymentInstallment || 1),
          currency: "TRY",
          returnUrl: returnUrl,
          clientOrderReference: clientOrderReference,
          orderNo: orderNo,

          payment_source: this.paymentSource || "manuel",

          customer_vkn: vno,

          customer: {
            name: baslik,
            email: customerEmail,
            phone: customerPhone,
            carid: carid,
            tckn: vno
          },

          order: {
            description: "Cari tahsilat"
          },

          customer_message: "Cari tahsilat",

          card: {
            holder: form.nm,
            number: form.numDigits,
            expMonth: form.mm,
            expYear: form.yy,
            cvc: form.cv
          }
        };

        console.log("PAYLOAD TO INIT =", {
          amount: Number(this.paymentAmount || 0),
          feeAmount: Number(this.paymentFeeAmount || 0),
          installment: Number(this.paymentInstallment || 1),
          selectedInstallment: this.selectedInstallment
        });

        const res = await this.$request.post("payment/init", payload);
        const data = res && res.data ? res.data : res;

        if (!data || data.success === false) {
          throw new Error((data && data.message) || "Ödeme başlatılamadı");
        }

        this.lastOrderNo = data.orderNo || orderNo;
        this.lastRedirectUrl = data.redirectUrl || "";

        if (data.mode === "redirect" && data.redirectUrl) {
          document.location = data.redirectUrl;
          return;
        }

if (
  data &&
  (
    data.mode === "html_form" ||
    (data.data && data.data.mode === "html_form")
  )
) {
  var payload = data.data ? data.data : data;

  console.log("INIT RESPONSE =", payload);
  console.log("HTML LENGTH =", payload && payload.html ? payload.html.length : 0);

  if (!payload || !payload.html) {
    console.error("HTML boş geldi");
    return;
  }

  var wrapper = document.createElement("div");
  wrapper.innerHTML = payload.html;
  document.body.appendChild(wrapper);

  var formEl = wrapper.querySelector("form");

  if (!formEl) {
    console.error("FORM BULUNAMADI");
    console.log(wrapper.innerHTML);
    return;
  }

  console.log("FORM ACTION =", formEl.action);

  setTimeout(function () {
    formEl.submit();
  }, 50);

  return;
}

        // Redirect / HTML form degilse (3D'siz veya aninda tamamlanan akislarda)
        // artik transaction-check yapmiyoruz; sonucu direkt sonuc sayfasinda gosteriyoruz.
        try {
          const providerCode = String(
            (this.providerInfo && (this.providerInfo.code || this.providerInfo.provider_code)) ||
            "finrota"
          ).toLowerCase();

          sessionStorage.setItem(
            "payment_result",
            JSON.stringify({
              success: false,
              status: (data && data.status) ? String(data.status) : "3d_pending",
              orderNo: this.lastOrderNo || orderNo,
              clientOrderReference: clientOrderReference,
              provider: { code: providerCode, name: providerCode, type: "" },
              payment_source: payload.payment_source || "manuel",
              customer: payload.customer || {},
              amounts: {
                installment: Number(this.paymentInstallment || 1),
                amount: Number(this.enteredAmount || 0),
                feeAmount: Number(this.paymentFeeAmount || 0),
                totalAmount: Number(this.paymentTotalAmount || this.enteredAmount || 0),
                currency: "TRY"
              },
              errorCode: null,
              errorMessage: null
            })
          );
        } catch (e) { }

        this.$router.push({ path: "/tahsilat/sonuc" });
        return;
      } catch (err) {
        console.error("PAY_INIT_ERROR", err);
        this.payError =
          (err && err.message) ||
          "Ödeme başlatılırken hata oluştu";
        alert(this.payError);
      } finally {
        this.payLoading = false;
        this.$store.setLoading([false, ""]);
      }
    }
  }
};
</script>

<style scoped>
.wrap{
  max-width: 1320px;
  margin: 22px auto;
  padding: 0 14px;
}
.grid{
  display:grid;
  grid-template-columns: 1.1fr 1fr .85fr;
  gap: 16px;
  align-items:start;
  padding-bottom:50px
}
@media (max-width: 1100px){
  .grid{ grid-template-columns: 1fr; }
}

.panel{
  background:#fff;
  border:1px solid #e8ecf5;
  border-radius:18px;
  box-shadow: 0 14px 40px rgba(16,24,40,.10);
  overflow:hidden;
}
.ph{
  padding: 14px 16px;
  border-bottom: 1px solid #e8ecf5;
  background: linear-gradient(180deg, #fff, #fbfcff);
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
}
.ph h2{
  margin:0;
  font-size: 13px;
  letter-spacing:.2px;
  text-transform: uppercase;
  color:#64748b;
  font-weight: 700;
}

.pill{
  display:inline-flex;
  align-items:center;
  gap:8px;
  padding: 7px 10px;
  border-radius: 999px;
  border: 1px solid #dfe6f3;
  background: #fff;
  font-weight: 600;
  font-size: 12px;
  color:#334155;
  white-space:nowrap;
}
.dot{
  width:9px;height:9px;border-radius:999px;
  background:#cbd5e1;
}
.dot.ok{ background:#16a34a; }

.card-area{ padding:16px; display:grid; gap:12px; }
.card-3d{ width:100%; max-width: 420px; perspective: 1200px; }
.card{
  width:100%; height:200px;
  position:relative;
  transform-style:preserve-3d;
  transition: transform .6s cubic-bezier(.2,.9,.2,1);
}
.card.flipped{ transform: rotateY(180deg); }

.face{
  position:absolute; inset:0;
  border-radius: 18px;
  border:1px solid rgba(15,23,42,.10);
  overflow:hidden;
  backface-visibility:hidden;
  background:
    radial-gradient(520px 240px at 20% 0%, rgba(16,42,87,.18), transparent 55%),
    radial-gradient(620px 320px at 90% 80%, rgba(99,102,241,.10), transparent 55%),
    linear-gradient(135deg, rgba(255,255,255,.88), rgba(245,248,255,.86));
  box-shadow: 0 18px 40px rgba(15,23,42,.10);
}
.front{ padding:18px; }
.top{ display:flex; align-items:center; gap:12px; }
.chip{
  width:54px; height:40px;
  border-radius: 12px;
  border:1px solid rgba(15,23,42,.10);
  background: linear-gradient(180deg, rgba(15,23,42,.07), rgba(15,23,42,.03));
}
.brandText{
  margin-left:auto;
  font-weight: 700;
  letter-spacing: 1px;
  font-size: 12px;
  color: rgba(15,23,42,.70);
  text-transform: uppercase;
}
.num{
  margin-top:22px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace;
  font-size:20px;
  letter-spacing: 1.6px;
  color: rgba(15,23,42,.82);
  min-height:28px;
  font-weight: 600;
}
.meta{
  margin-top:18px;
  display:flex;
  justify-content:space-between;
  gap:18px;
}
.lbl{
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .6px;
  color: rgba(100,116,139,.95);
  font-weight: 600;
}
.val{
  margin-top:6px;
  font-size: 14px;
  font-weight: 700;
  color: rgba(15,23,42,.82);
  letter-spacing: .2px;
}

.back{ transform: rotateY(180deg); }
.mag{ margin-top:18px; height:42px; background: rgba(15,23,42,.20); }
.sig{
  margin:16px 18px 0 18px;
  height:44px;
  border-radius:12px;
  border:1px solid rgba(15,23,42,.10);
  background: rgba(255,255,255,.70);
  display:flex;
  align-items:center;
  justify-content:flex-end;
  padding:0 12px;
  gap:10px;
}
.muted{ color:#64748b; font-weight:600; }
.cvc-box{
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-weight: 700;
  letter-spacing: 1px;
  color: rgba(15,23,42,.82);
  background: rgba(15,23,42,.08);
  border:1px solid rgba(15,23,42,.10);
  padding:6px 10px;
  border-radius:10px;
  min-width:66px;
  text-align:center;
}
.hint{
  margin:14px 18px;
  font-size: 12px;
  color: rgba(100,116,139,.95);
  font-weight: 600;
}

.form{
  display:grid;
  grid-template-columns: 1fr 1fr;
  gap:12px;
  margin-top: 4px;
}
@media (max-width: 520px){
  .form{ grid-template-columns: 1fr; }
}
.field{ display:flex; flex-direction:column; gap:6px; min-width:0; }
.field label{
  font-size:12px;
  color:#64748b;
  font-weight: 600;
  letter-spacing:.2px;
}
.input{
  border-radius: 14px;
  border:1px solid #dfe6f3;
  background: #f7f9ff;
  padding: 12px 12px;
  outline:none;
  font-weight: 600;
  color:#0f172a;
  transition: box-shadow .15s ease, border-color .15s ease, background .15s ease;
}
.input:focus{
  background:#fff;
  border-color: rgba(29,78,216,.35);
  box-shadow: 0 0 0 4px rgba(29,78,216,.12);
}
.input:disabled{
  background:#eef2f7;
  color:#94a3b8;
  cursor:not-allowed;
}
.span2{ grid-column: 1 / -1; }

.pay-form-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  width: 100%;
}
.pay-form-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.pay-label {
  display: block;
  font-size: 12px;
  line-height: 1.2;
  font-weight: 700;
  color: #64748b;
  margin: 0;
}
.fa-input {
  width: 100%;
  height: 46px;
  padding: 0 12px;
  border: 1px solid #dbe2ea;
  border-radius: 12px;
  background: #fff;
  color: #0f172a;
  font-size: 14px;
  font-weight: 600;
  outline: none;
  box-sizing: border-box;
  transition: border-color .15s ease, box-shadow .15s ease, background-color .15s ease;
}
.fa-input:focus {
  border-color: rgba(37, 99, 235, .35);
  box-shadow: 0 0 0 4px rgba(37, 99, 235, .12);
}
.form-hint {
  margin: 0 0 6px;
  font-size: 12px;
  line-height: 1.4;
}
.form-hint--warn {
  color: #b45309;
}
@media (max-width: 1100px) {
  .pay-form-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 640px) {
  .pay-form-grid {
    grid-template-columns: 1fr;
  }
}

.mid{ padding: 10px 16px 16px 16px; }
.info{
  display:flex; align-items:center; justify-content:space-between; gap:10px;
  padding:12px 12px;
  border-radius: 16px;
  border:1px solid #e8ecf5;
  background: linear-gradient(180deg, #f7f9ff, #fff);
}
.k{
  font-size:12px;
  color:#64748b;
  font-weight: 600;
  text-transform:uppercase;
  letter-spacing:.3px;
}
.v{
  font-size:13px;
  font-weight: 700;
  color:#0f172a;
}
.inst-list{ margin-top: 12px; display:grid; gap:10px; }

.plan-card {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 14px;
  border: 1px solid #e5eaf1;
  border-radius: 16px;
  background: #fff;
  padding: 14px 16px;
  cursor: pointer;
  transition: all .15s ease;
}
.plan-card.active {
  border-color: #2563eb;
  box-shadow: 0 0 0 4px rgba(37, 99, 235, .10);
}
.plan-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.plan-number {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
}
.plan-text { min-width: 0; }
.plan-title {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
}
.plan-sub {
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
}
.plan-right { text-align: right; }
.plan-monthly {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
}
.plan-total {
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
}
.plan-radio {
  display: flex;
  align-items: center;
  justify-content: center;
}
.radio-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #cbd5e1;
  display: inline-block;
}
.radio-dot.active {
  border-color: #2563eb;
  box-shadow: inset 0 0 0 4px #2563eb;
}
.empty{
  margin-top:12px;
  border:1px dashed #dfe6f3;
  border-radius: 16px;
  padding: 14px 12px;
  background:#fff;
  color:#64748b;
  font-weight: 600;
  font-size: 13px;
}

.sum{ padding: 16px; }
.brandbox{
  border-radius: 18px;
  border:1px solid #e8ecf5;
  background: linear-gradient(180deg, #fff, #fbfcff);
  padding: 14px;
}
.brandtop{ display:flex; gap:12px; align-items:center; }
.logo{
  width:60px;
  height:60px;
  border-radius: 14px;
  border:1px solid #dfe6f3;
  background: linear-gradient(180deg, #fff, #f7f9ff);
  display:grid; place-items:center;
  color:#94a3b8;
  font-weight: 700;
}
.logo img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.brandtxt .h{ font-weight:700; color:#0f172a; font-size:14px; }
.brandtxt .s{ margin-top:4px; color:#64748b; font-weight:600; font-size:12px; }
.linkbox{
  margin-top:12px;
  border:1px solid #e8ecf5;
  border-radius: 16px;
  background:#fff;
  padding: 12px;
  display:flex; align-items:center; justify-content:space-between; gap:10px;
  color:#64748b;
  font-weight: 600;
}
.lines{
  margin-top:12px;
  border-top:1px solid #e8ecf5;
  padding-top: 12px;
  display:grid;
  gap:10px;
}
.line{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding: 10px 2px;
  border-bottom:1px solid rgba(232,236,245,.55);
}
.line:last-child{ border-bottom:none; }

.paybtn{
  width:100%;
  margin-top: 14px;
  border:none;
  border-radius: 16px;
  padding: 14px 14px;
  background: #1d4ed8;
  color:#fff;
  font-weight: 700;
  cursor:pointer;
  box-shadow: 0 14px 26px rgba(29,78,216,.22);
  display:flex;
  align-items:center;
  justify-content:center;
  gap:10px;
}
.paybtn:disabled{
  opacity:.65;
  cursor:not-allowed;
}
.lock{
  width:34px; height:34px;
  border-radius: 12px;
  background: rgba(255,255,255,.12);
  display:grid; place-items:center;
  margin-left:auto;
}
</style>



// WEBPACK FOOTER //
// src/views/tahsilat/add.vue